import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Account, Tenant } from 'src/models/account.model';
import { TenantService } from 'src/services/tenant.service';
import { TenantPublicService } from 'src/services/tenant-public.service';
import Swal from 'sweetalert2';

interface TenantSession {
  tenant_id: string;
  account_key: string;
  store_name: string;
  tenant_name: string;
  username: string;
  logged_in_at: Date;
  expires_at: Date;
}

interface PreAuthSession {
  token: string;
  tenant_id: string;
  username: string;
  pin: string;
  account_key: string;
  created_at: Date;
  expires_at: Date;
}

@Component({
  selector: 'app-tenant-login',
  templateUrl: './tenant-login.component.html',
  styleUrls: ['./tenant-login.component.css']
})
export class TenantLoginComponent implements OnInit {

  loginForm: FormGroup;
  accountKey: string = '';
  account: Account | null = null;
  isLoading: boolean = false;
  showPin: boolean = false;
  loginAttempts: number = 0;
  maxAttempts: number = 5;
  isLocked: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private tenantService: TenantService,
    private tenantPublicService: TenantPublicService
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      pin: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  async ngOnInit() {
    // Get account_key from parent route since it's defined in parent route config
    this.accountKey = this.route.parent?.snapshot.paramMap.get('account_key') || '';

    console.log('TenantLogin - Full URL:', window.location.href);
    console.log('TenantLogin - Route params (child):', this.route.snapshot.paramMap.keys);
    console.log('TenantLogin - Route params (parent):', this.route.parent?.snapshot.paramMap.keys);
    console.log('TenantLogin - Account Key from route:', this.accountKey);

    // Check for pre-auth token first (admin direct access)
    const preAuthHandled = await this.checkPreAuthToken();
    if (preAuthHandled) {
      return; // Stop here if pre-auth handled the login
    }

    // Check if already logged in
    const session = localStorage.getItem('tenant_session');
    if (session) {
      const tenantSession: TenantSession = JSON.parse(session);
      if (tenantSession.account_key === this.accountKey && new Date(tenantSession.expires_at) > new Date()) {
        this.router.navigate([`/tenant/${this.accountKey}/generate`]);
        return;
      }
    }

    // Load account
    await this.loadAccount();
  }

  async loadAccount() {
    this.isLoading = true;
    try {
      console.log('Loading account with key (public):', this.accountKey);
      // Use public service that doesn't require Firebase authentication
      this.account = await this.tenantPublicService.getAccountByKeyPublic(this.accountKey);
      console.log('Account loaded:', this.account);

      if (!this.account) {
        console.error('No account found with account_key:', this.accountKey);
        Swal.fire({
          icon: 'error',
          title: 'Invalid Portal',
          html: `
            <p>This tenant portal does not exist or is unavailable.</p>
            <p class="text-sm text-gray-500 mt-2">Account Key: ${this.accountKey}</p>
            <p class="text-xs text-gray-400 mt-2">Please verify the URL with your administrator.</p>
          `,
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error loading account:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: `
          <p>Failed to load portal.</p>
          <p class="text-sm text-gray-500 mt-2">Error: ${error.message || 'Unknown error'}</p>
        `,
        confirmButtonText: 'OK'
      });
    }
    this.isLoading = false;
  }

  toggleShowPin() {
    this.showPin = !this.showPin;
  }

  async login() {
    if (this.isLocked) {
      Swal.fire({
        icon: 'warning',
        title: 'Account Locked',
        text: 'Too many failed attempts. Please try again in 15 minutes.'
      });
      return;
    }

    if (this.loginForm.invalid) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Input',
        text: 'Please enter username and 6-digit PIN.'
      });
      return;
    }

    this.isLoading = true;

    // Fetch fresh account data to get newly added tenants
    try {
      this.account = await this.tenantPublicService.getAccountByKeyPublic(this.accountKey);
    } catch (error) {
      console.error('Error fetching account:', error);
    }

    if (!this.account) {
      this.isLoading = false;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Portal not loaded. Please refresh the page.'
      });
      return;
    }

    const username = this.loginForm.get('username').value.toLowerCase().trim();
    const pin = this.loginForm.get('pin').value;

    const tenant = this.tenantService.validateTenantLogin(this.account, username, pin);

    if (!tenant) {
      this.isLoading = false;
      this.loginAttempts++;

      if (this.loginAttempts >= this.maxAttempts) {
        this.isLocked = true;
        setTimeout(() => {
          this.isLocked = false;
          this.loginAttempts = 0;
        }, 15 * 60 * 1000); // 15 minutes lockout

        Swal.fire({
          icon: 'error',
          title: 'Account Locked',
          text: 'Too many failed attempts. Please try again in 15 minutes.'
        });
        return;
      }

      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: `Invalid username or PIN. ${this.maxAttempts - this.loginAttempts} attempts remaining.`
      });
      return;
    }

    this.isLoading = false;

    // Create session
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year (effectively no expiration)

    const session: TenantSession = {
      tenant_id: tenant.tenant_id,
      account_key: this.accountKey,
      store_name: tenant.store_name,
      tenant_name: tenant.tenant_name,
      username: tenant.username,
      logged_in_at: now,
      expires_at: expiresAt
    };

    localStorage.setItem('tenant_session', JSON.stringify(session));

    // Update last login
    try {
      await this.tenantService.updateLastLogin(this.account, tenant.tenant_id);
    } catch (error) {
      console.error('Error updating last login:', error);
    }

    Swal.fire({
      icon: 'success',
      title: 'Welcome!',
      text: `Logged in as ${tenant.store_name}`,
      timer: 1500,
      showConfirmButton: false
    }).then(() => {
      this.router.navigate([`/tenant/${this.accountKey}/generate`]);
    });
  }

  /**
   * Check for pre-auth token in URL query params
   * Returns true if pre-auth was handled, false otherwise
   */
  async checkPreAuthToken(): Promise<boolean> {
    const preAuthToken = this.route.snapshot.queryParamMap.get('preauth_token');
    if (!preAuthToken) {
      return false; // No pre-auth token present
    }

    console.log('Pre-auth token detected:', preAuthToken);

    // Retrieve pre-auth session from sessionStorage
    const preAuthKey = `preauth_${preAuthToken}`;
    const preAuthData = sessionStorage.getItem(preAuthKey);

    if (!preAuthData) {
      console.error('Pre-auth token not found in sessionStorage');
      await Swal.fire({
        icon: 'error',
        title: 'Invalid Access Link',
        text: 'The portal access link is invalid or has expired. Please request a new link from the admin panel.',
        confirmButtonText: 'OK'
      });
      // Remove token from URL
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true
      });
      return false;
    }

    try {
      const preAuthSession: PreAuthSession = JSON.parse(preAuthData);

      // Validate token expiration (30 days)
      const now = new Date();
      const expiresAt = new Date(preAuthSession.expires_at);
      if (now > expiresAt) {
        console.error('Pre-auth token expired');
        sessionStorage.removeItem(preAuthKey);
        await Swal.fire({
          icon: 'warning',
          title: 'Link Expired',
          text: 'The portal access link has expired. Please request a new link from the admin panel.',
          confirmButtonText: 'OK'
        });
        this.router.navigate([], {
          queryParams: {},
          replaceUrl: true
        });
        return false;
      }

      // Validate account key matches
      if (preAuthSession.account_key !== this.accountKey) {
        console.error('Account key mismatch');
        sessionStorage.removeItem(preAuthKey);
        await Swal.fire({
          icon: 'error',
          title: 'Invalid Access',
          text: 'Access link does not match this portal.',
          confirmButtonText: 'OK'
        });
        this.router.navigate([], {
          queryParams: {},
          replaceUrl: true
        });
        return false;
      }

      // Token is valid - proceed with auto-login
      console.log('Pre-auth token valid, attempting auto-login');

      // Clear token immediately (one-time use)
      sessionStorage.removeItem(preAuthKey);

      // Remove token from URL before auto-login
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true
      });

      // Perform auto-login
      await this.autoLoginWithPreAuth(preAuthSession);
      return true;

    } catch (error) {
      console.error('Error processing pre-auth token:', error);
      sessionStorage.removeItem(preAuthKey);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to process access link. Please try again.',
        confirmButtonText: 'OK'
      });
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true
      });
      return false;
    }
  }

  /**
   * Auto-login using pre-auth session data
   */
  async autoLoginWithPreAuth(preAuthSession: PreAuthSession): Promise<void> {
    this.isLoading = true;

    // Load account data
    try {
      this.account = await this.tenantPublicService.getAccountByKeyPublic(this.accountKey);
    } catch (error) {
      console.error('Error loading account:', error);
      this.isLoading = false;
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load portal. Please try again.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!this.account) {
      this.isLoading = false;
      await Swal.fire({
        icon: 'error',
        title: 'Portal Not Found',
        text: 'This portal does not exist or is unavailable.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate tenant credentials from pre-auth session
    const tenant = this.tenantService.validateTenantLogin(
      this.account,
      preAuthSession.username,
      preAuthSession.pin
    );

    if (!tenant) {
      this.isLoading = false;
      await Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: 'Tenant credentials are invalid. The tenant may have been updated or removed.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Create tenant session
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year (effectively no expiration)

    const session: TenantSession = {
      tenant_id: tenant.tenant_id,
      account_key: this.accountKey,
      store_name: tenant.store_name,
      tenant_name: tenant.tenant_name,
      username: tenant.username,
      logged_in_at: now,
      expires_at: expiresAt
    };

    localStorage.setItem('tenant_session', JSON.stringify(session));

    // Update last login
    try {
      await this.tenantService.updateLastLogin(this.account, tenant.tenant_id);
    } catch (error) {
      console.error('Error updating last login:', error);
    }

    this.isLoading = false;

    // Show success and redirect
    await Swal.fire({
      icon: 'success',
      title: 'Welcome!',
      text: `Logged in as ${tenant.store_name}`,
      timer: 1500,
      showConfirmButton: false
    });

    this.router.navigate([`/tenant/${this.accountKey}/generate`]);
  }
}
